import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/shared-ui.css";
import { useAppContext } from "../context/AppContext";
import { getCompanyNames } from "../services/companyService";
import { getFunctionsByCompany } from "../services/functionService";
import { getJobCategories } from "../services/jobCategoryService";
import { getJobStatuses } from "../services/jobStatusService";
import { getJobWithRelations, updateJob, generateJobDescription, getJobsByCompanyId } from "../services/jobService";
import { getTasksByCompanyId } from "../services/taskService";
import { toast } from "../utils/toast";
import RichTextEditor from "../components/ui/RichTextEditor";
import SideTabs from "../components/layout/SideTabs";
import SkillTable from "../components/ui/SkillTable";
import SkillAddModal from "../components/ui/SkillAddModal";
import RaciMatrix from "../components/ui/RaciMatrix";
import CurrencySelector from "../components/ui/CurrencySelector";
import { getCurrencySymbol } from "../services/currencyService";
import { LEVELS } from "../components/ui/levels";
import { useLocation, useParams } from "react-router-dom";
import DimensionAllocationPanel from "../components/ui/DimensionAllocationPanel";
import {
  PAEI_DEFAULTS,
  PAEI_DIMENSIONS,
  CAPI_DEFAULTS,
  CAPI_DIMENSIONS,
} from "../components/ui/dimensionAllocations";
import { getPaeiByJobId, savePaeiForJob } from "../services/paeiService";
import { getCapiByJobId, saveCapiForJob } from "../services/capiService";

const extractTaskJoinRows = (job) => {
  const taskJobs =
    job?.taskJobs || job?.task_jobs || job?.jobTasks || job?.job_tasks || [];
  return Array.isArray(taskJobs) ? taskJobs : [];
};

const extractRaciRows = (job) =>
  extractTaskJoinRows(job)
    .map((tj) => ({
      task_id: tj?.task_id ?? tj?.task?.task_id ?? tj?.task?.id,
      role: tj?.role ?? null,
      time_allocation_percentage:
        tj?.time_allocation_percentage != null
          ? Number(tj.time_allocation_percentage)
          : null,
    }))
    .filter((r) => r.task_id != null);

const JobEditPage = () => {
  const { jobId, setJobId, setActiveSection } = useAppContext();
  const { jobId: jobIdParam } = useParams();
  const location = useLocation();
  const routeJobId = useMemo(() => {
    if (jobIdParam != null && jobIdParam !== "") {
      try {
        return decodeURIComponent(jobIdParam);
      } catch {
        return jobIdParam;
      }
    }
    const pathname = String(location?.pathname || "");
    const match = pathname.match(/^\/jobs\/([^/]+)\/edit$/i);
    if (!match?.[1]) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }, [jobIdParam, location?.pathname]);

  const [companies, setCompanies] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loadingFunctions, setLoadingFunctions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingDesc, setGeneratingDesc] = useState(false);
  const [showAddSkill, setShowAddSkill] = useState(false);
  const [allTasks, setAllTasks] = useState([]);
  const [raciRows, setRaciRows] = useState([]);
  const [jobCategories, setJobCategories] = useState([]);
  const [jobStatuses, setJobStatuses] = useState([]);
  const [companyJobs, setCompanyJobs] = useState([]);   // all jobs in selected company for parent picker
  const [currentJobId, setCurrentJobId] = useState(null); // id of the job being edited (excluded from parent list)
  const [paeiValues, setPaeiValues] = useState(PAEI_DEFAULTS);
  const [paeiRecordId, setPaeiRecordId] = useState(null);
  const [capiValues, setCapiValues] = useState(CAPI_DEFAULTS);
  const [capiRecordId, setCapiRecordId] = useState(null);

  const [form, setForm] = useState({
    jobCode: "",
    name: "",
    description: "",
    company_id: "",
    function_id: "",
    category_id: "",
    status_id: "",
    hourlyRate: "",
    currencyType: "USD",
    maxHoursPerDay: "",
    staff_count: "",
    daysPerWeek: "5",
    capacityBuffer: "10",
    jobLevel: "",
    skills: [],
    parent_job_id: "",   // "" means no parent
  });
  const didInit = useRef(false);

  // fetch lists and job
  useEffect(() => {
    (async () => {
      if (didInit.current) return; // prevent StrictMode double invoke
      didInit.current = true;
      try {
        setLoading(true);
        const companiesRes = await getCompanyNames().catch(() => []);
        setCompanies(Array.isArray(companiesRes) ? companiesRes : []);
        setFunctions([]);

        // Fetch job categories
        try {
          const categoriesRes = await getJobCategories();
          setJobCategories(Array.isArray(categoriesRes) ? categoriesRes : []);
        } catch {
          setJobCategories([]);
        }
        try {
          const statusesRes = await getJobStatuses();
          setJobStatuses(Array.isArray(statusesRes) ? statusesRes : []);
        } catch {
          setJobStatuses([]);
        }

        // Tasks will be loaded when company is selected
        setAllTasks([]);

        let id = routeJobId || jobId;
        if (routeJobId) {
          try {
            localStorage.setItem("activeJobId", String(routeJobId));
          } catch {}
          setJobId(routeJobId);
        }
        if (!id) {
          try {
            const stored = localStorage.getItem("activeJobId");
            if (stored) {
              id = stored;
              setJobId(stored);
            }
          } catch {}
        }
        if (!id) throw new Error("Missing job id");

        const data = await getJobWithRelations(id);
        if (data) {
          const nextCompanyId =
            data.company_id != null ? String(data.company_id) : "";
          setForm({
            jobCode: data.jobCode || data.job_code || "",
            name: data.name || "",
            description: data.description || "",
            company_id: nextCompanyId,
            function_id:
              data.function_id != null ? String(data.function_id) : "",
            category_id:
              data.category_id != null ? String(data.category_id) : "",
            status_id: data.status_id != null ? String(data.status_id) : "",
            hourlyRate: data.hourlyRate != null ? String(data.hourlyRate) : "",
            currencyType: data.currencyType || "USD",
            maxHoursPerDay:
              data.maxHoursPerDay != null ? String(data.maxHoursPerDay) : "",
            staff_count:
              data.staff_count != null ? String(data.staff_count) : "",
            daysPerWeek:
              data.days_per_week != null ? String(data.days_per_week)
              : data.daysPerWeek != null ? String(data.daysPerWeek)
              : "5",
            capacityBuffer:
              data.capacity_buffer != null ? String(data.capacity_buffer)
              : data.capacityBuffer != null ? String(data.capacityBuffer)
              : "10",
            parent_job_id:
              data.parent_job_id != null ? String(data.parent_job_id) : "",
            jobLevel: data.jobLevel || data.job_level?.level_name || "",
            skills:
              Array.isArray(data.jobSkills) ?
                data.jobSkills.map((js) => ({
                  name: js?.skill?.name || "",
                  level: js?.skill_level?.level_name || "",
                }))
              : [],
          });
          if (nextCompanyId) {
            await loadFunctionsForCompany(nextCompanyId, true);
            // Load all company jobs for the parent picker
            try {
              const allJobs = await getJobsByCompanyId(nextCompanyId);
              setCompanyJobs(Array.isArray(allJobs) ? allJobs : []);
            } catch {
              setCompanyJobs([]);
            }
          }
          // Remember the id of this job so we exclude it from the parent list
          const resolvedJobId = data.job_id ?? data.id ?? id;
          setCurrentJobId(String(resolvedJobId ?? ""));
          setRaciRows(extractRaciRows(data));

          const [existingPaei, existingCapi] = await Promise.all([
            getPaeiByJobId(resolvedJobId),
            getCapiByJobId(resolvedJobId),
          ]);
          if (existingPaei) {
            setPaeiValues({
              producer: existingPaei.producer,
              administrator: existingPaei.administrator,
              entrepreneur: existingPaei.entrepreneur,
              integrator: existingPaei.integrator,
            });
            setPaeiRecordId(existingPaei.id);
          }
          if (existingCapi) {
            setCapiValues({
              authority: existingCapi.authority,
              power: existingCapi.power,
              influence: existingCapi.influence,
            });
            setCapiRecordId(existingCapi.id);
          }
        } else {
          setRaciRows([]);
        }
      } catch (e) {
        toast.error(e?.message || "Failed to load job");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId, routeJobId, setJobId]);

  const onBack = () => {
    const returnTo = localStorage.getItem("returnTo");
    if (returnTo === "unassigned-entities") {
      localStorage.removeItem("returnTo");
      setActiveSection("unassigned-entities");
    } else {
      setActiveSection("job-management");
    }
  };
  const setField = (key, val) => setForm((prev) => ({ ...prev, [key]: val }));

  const setPaeiField = (key, val) =>
    setPaeiValues((prev) => ({ ...prev, [key]: val }));
  const setCapiField = (key, val) =>
    setCapiValues((prev) => ({ ...prev, [key]: val }));

  const removeSkill = (index) => {
    setForm((prev) => ({
      ...prev,
      skills: prev.skills.filter((_, i) => i !== index),
    }));
  };

  const loadFunctionsForCompany = async (companyId, keepFunctionId) => {
    if (!companyId) {
      setFunctions([]);
      setAllTasks([]);
      setCompanyJobs([]);
      setLoadingFunctions(false);
      if (!keepFunctionId) {
        setForm((prev) => ({ ...prev, function_id: "", parent_job_id: "" }));
      }
      return;
    }
    try {
      setLoadingFunctions(true);
      const [fn, tasks, jobs] = await Promise.all([
        getFunctionsByCompany(companyId).catch(() => []),
        getTasksByCompanyId(companyId).catch(() => []),
        getJobsByCompanyId(companyId).catch(() => []),
      ]);
      setFunctions(Array.isArray(fn) ? fn : []);
      setAllTasks(Array.isArray(tasks) ? tasks : []);
      setCompanyJobs(Array.isArray(jobs) ? jobs : []);
      if (!keepFunctionId) {
        setForm((prev) => ({ ...prev, function_id: "", parent_job_id: "" }));
      }
    } finally {
      setLoadingFunctions(false);
    }
  };

  const canSave = useMemo(() => {
    const descText = String(form.description || "")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .trim();
    const hr = Number(form.hourlyRate);
    const mh = Number(form.maxHoursPerDay);
    return (
      String(form.name).trim() &&
      String(form.jobCode).trim() &&
      String(form.company_id) &&
      String(form.function_id) &&
      String(form.jobLevel) &&
      descText.length >= 10 &&
      form.hourlyRate !== "" &&
      Number.isFinite(hr) &&
      hr >= 0 &&
      form.maxHoursPerDay !== "" &&
      Number.isInteger(mh) &&
      mh >= 1 &&
      String(form.category_id) &&
      String(form.status_id)
    );
  }, [form]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSave || saving) return;
    try {
      setSaving(true); // determine id
      let id = routeJobId || jobId;
      if (!id) {
        try {
          id = localStorage.getItem("activeJobId") || id;
        } catch {}
      }
      if (!id) throw new Error("Missing job id");

      const payload = {
        jobCode: form.jobCode,
        name: form.name,
        description: form.description,
        company_id: form.company_id,
        function_id: form.function_id,
        category_id: form.category_id ? Number(form.category_id) : null,
        status_id: form.status_id ? Number(form.status_id) : null,
        // Saved as-is in the selected currency (no conversion).
        hourlyRate: form.hourlyRate === "" ? null : Number(form.hourlyRate),
        currencyType: form.currencyType,
        maxHoursPerDay:
          form.maxHoursPerDay === "" ? null : Number(form.maxHoursPerDay),
        staff_count:
          form.staff_count === "" ? undefined : Number(form.staff_count),
        daysPerWeek:
          form.daysPerWeek === "" ? null : Number(form.daysPerWeek),
        capacityBuffer:
          form.capacityBuffer === "" ? null : Number(form.capacityBuffer),
        jobLevel: form.jobLevel,
        // parent_job_id: "" means "no parent" — send null; otherwise send the numeric id
        parent_job_id: form.parent_job_id !== "" ? Number(form.parent_job_id) : null,
        skills: (form.skills || [])
          .filter((s) => String(s.name).trim() && String(s.level))
          .map((s) => ({ name: s.name.trim(), level: s.level })),
        raci: raciRows,
      };
      await updateJob(id, payload);

      // PAEI/CAPI live on their own backend tables (not nested under the job
      // payload), so they're saved as separate calls. A failure here doesn't
      // roll back the job update above — it's already saved — so report it
      // as its own toast instead of blocking navigation away from the page.
      try {
        const savedPaei = await savePaeiForJob(id, paeiValues, paeiRecordId);
        if (savedPaei?.id) setPaeiRecordId(savedPaei.id);
      } catch (paeiErr) {
        toast.error(paeiErr?.message || "Failed to save PAEI data");
      }
      try {
        const savedCapi = await saveCapiForJob(id, capiValues, capiRecordId);
        if (savedCapi?.id) setCapiRecordId(savedCapi.id);
      } catch (capiErr) {
        toast.error(capiErr?.message || "Failed to save CAPI data");
      }

      toast.success(`Job "${payload.name}" updated successfully.`);
      const returnTo = localStorage.getItem("returnTo");
      if (returnTo === "unassigned-entities") {
        localStorage.removeItem("returnTo");
        setActiveSection("unassigned-entities");
      } else {
        setActiveSection("job-management");
      }
    } catch (e) {
      const message = e?.message || "Failed to update job";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateDescription = async () => {
    if (generatingDesc) return;
    const taskPayload = raciRows
      .map((row) => {
        const task = allTasks.find(
          (t) => String(t.task_id) === String(row.task_id)
        );
        if (!task) return null;
        return {
          taskName: task.task_name || task.name || "",
          taskOverview: task.task_overview || task.overview || "",
          role: row.role || null,
        };
      })
      .filter(Boolean);

    if (taskPayload.length === 0) {
      toast.error("No tasks assigned in the RACI tab yet. Add tasks first, then generate a description.");
      return;
    }

    try {
      setGeneratingDesc(true);
      const description = await generateJobDescription({
        jobName: form.name || undefined,
        tasks: taskPayload,
      });
      setField("description", description);
      toast.success("Description generated! Review and edit it before saving.");
    } catch (e) {
      toast.error(e?.message || "Failed to generate description");
    } finally {
      setGeneratingDesc(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="page-content">
          <div className="no-results">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Edit Job</h2>
            <p className="page-subtitle">
              {form.name ?
                `Editing: ${form.name}`
              : "Update job info, level, description, and skills"}
              {form.jobCode ? ` (${form.jobCode})` : ""}
            </p>
          </div>
          <div className="roles-toolbar">
            <button className="secondary-btn" onClick={onBack}>
              Cancel
            </button>
            <button
              className="primary-btn"
              onClick={onSubmit}
              disabled={!canSave || saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <SideTabs
          defaultActiveId="basic"
          tabs={[
            {
              id: "basic",
              label: "Basic Details",
              content: (
                <div className="role-card">
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Job Name *</label>
                      <input
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder="e.g. Customer Onboarding"
                      />
                    </div>
                    <div className="form-group">
                      <label>Job Code *</label>
                      <input
                        value={form.jobCode}
                        onChange={(e) => setField("jobCode", e.target.value)}
                        placeholder="e.g. JOB-001"
                      />
                    </div>
                    <div className="form-group">
                      <label>Company *</label>
                      <select
                        value={form.company_id}
                        onChange={(e) => {
                          const val = e.target.value;
                          setField("company_id", val);
                          loadFunctionsForCompany(val);
                        }}
                      >
                        <option value="">Select Company</option>
                        {companies.map((c) => (
                          <option
                            key={c.company_id || c.id}
                            value={c.company_id || c.id}
                          >
                            {c.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Function *</label>
                      <select
                        value={form.function_id}
                        onChange={(e) =>
                          setField("function_id", e.target.value)
                        }
                        disabled={loadingFunctions}
                      >
                        <option value="">
                          {loadingFunctions ?
                            "Loading functions..."
                          : "Select Function"}
                        </option>
                        {functions.map((f) => (
                          <option key={f.function_id} value={f.function_id}>
                            {f.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* ── Parent Job ─────────────────────────────────────── */}
                    <div className="form-group">
                      <label htmlFor="job-parent-job">Parent Job</label>
                      <select
                        id="job-parent-job"
                        value={form.parent_job_id}
                        onChange={(e) => setField("parent_job_id", e.target.value)}
                        disabled={loadingFunctions}
                      >
                        <option value="">— No Parent (Root Job) —</option>
                        {companyJobs
                          .filter((j) => String(j.job_id ?? j.id) !== currentJobId)
                          .map((j) => (
                            <option key={j.job_id ?? j.id} value={j.job_id ?? j.id}>
                              {j.name || `Job #${j.job_id ?? j.id}`}
                              {j.jobCode || j.job_code ? ` (${j.jobCode || j.job_code})` : ""}
                            </option>
                          ))}
                      </select>
                      <small className="hint">
                        The job this position directly reports to in the hierarchy.
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Hourly Rate *</label>
                      <div className="currency-input-group">
                        <span className="currency-input-symbol">
                          {getCurrencySymbol(form.currencyType)}
                        </span>
                        <input
                          className="currency-input-field"
                          type="number"
                          value={form.hourlyRate}
                          onChange={(e) =>
                            setField("hourlyRate", e.target.value)
                          }
                          placeholder="e.g. 25"
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Currency</label>
                      <CurrencySelector
                        value={form.currencyType}
                        onChange={(code) => setField("currencyType", code)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Max Hours Per Day *</label>
                      <input
                        type="number"
                        min={1}
                        max={8}
                        step={1}
                        value={form.maxHoursPerDay}
                        onChange={(e) => {
                          const v =
                            e.target.value === "" ?
                              ""
                            : Math.max(1, Math.min(8, Number(e.target.value)));
                          setField("maxHoursPerDay", v);
                        }}
                        placeholder="e.g. 8"
                      />
                      <small className="hint">
                        Allowed range: 1 to 8 hours
                      </small>
                    </div>
                    <div className="form-group">
                      <label htmlFor="job-staff-count">Staff count</label>
                      <input
                        id="job-staff-count"
                        type="number"
                        min={1}
                        step={1}
                        value={form.staff_count}
                        onChange={(e) => setField("staff_count", e.target.value)}
                        placeholder="e.g. 3"
                      />
                      <small className="hint">
                        How many people hold this job.
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Days Per Week</label>
                      <input
                        type="number"
                        min={1}
                        max={6}
                        step={1}
                        value={form.daysPerWeek}
                        onChange={(e) => {
                          const v =
                            e.target.value === "" ?
                              ""
                            : Math.max(1, Math.min(6, Number(e.target.value)));
                          setField("daysPerWeek", v);
                        }}
                        placeholder="e.g. 5"
                      />
                      <small className="hint">Allowed range: 1 to 6 days</small>
                    </div>
                    <div className="form-group">
                      <label title="Share of the workday set aside for informal, non-task work like meetings, admin, or breaks">
                        Capacity Buffer (%)
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={form.capacityBuffer}
                        onChange={(e) => {
                          const v =
                            e.target.value === "" ?
                              ""
                            : Math.max(0, Math.min(100, Number(e.target.value)));
                          setField("capacityBuffer", v);
                        }}
                        placeholder="e.g. 10"
                      />
                      <small className="hint">
                        Portion of available hours held back for informal
                        work — meetings, admin, breaks — not counted toward
                        task capacity
                      </small>
                    </div>

                    <div className="form-group">
                      <label>Job Category *</label>
                      <select
                        value={form.category_id}
                        onChange={(e) =>
                          setField("category_id", e.target.value)
                        }
                      >
                        <option value="">Select job category</option>
                        {jobCategories.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.description || c.name || `Category ${c.id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Job Status *</label>
                      <select
                        value={form.status_id}
                        onChange={(e) => setField("status_id", e.target.value)}
                      >
                        <option value="">Select job status</option>
                        {jobStatuses.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.description || `Status ${s.id}`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Level Name *</label>
                      <select
                        value={form.jobLevel}
                        onChange={(e) => setField("jobLevel", e.target.value)}
                      >
                        <option value="">Select Level Name</option>
                        {LEVELS.map((l) => (
                          <option key={l.name} value={l.name}>
                            {l.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Level Rank</label>
                      <select
                        value={
                          LEVELS.find((l) => l.name === form.jobLevel)?.rank ||
                          ""
                        }
                        onChange={(e) =>
                          setField(
                            "jobLevel",
                            LEVELS.find(
                              (l) => l.rank === Number(e.target.value),
                            )?.name || "",
                          )
                        }
                      >
                        <option value="">Select Rank</option>
                        {LEVELS.map((l) => (
                          <option key={l.rank} value={l.rank}>
                            {l.rank}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group full">
                      <label>Level Description</label>
                      <input
                        disabled
                        value={
                          LEVELS.find((l) => l.name === form.jobLevel)
                            ?.description || ""
                        }
                      />
                    </div>
                  </div>
                </div>
              ),
            },
            {
              id: "skills",
              label: "Skills",
              content: (
                <div className="role-card">
                  <div
                    className="section-title"
                    style={{
                      marginBottom: 12,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <span>Skills</span>
                    <button
                      type="button"
                      className="primary-btn icon-square"
                      onClick={() => setShowAddSkill(true)}
                      aria-label="Add Skill"
                      title="Add Skill"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          d="M12 5v14M5 12h14"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                  <SkillTable
                    mode="manage"
                    skills={form.skills}
                    levels={LEVELS}
                    onDelete={(idx) => removeSkill(idx)}
                  />
                  <SkillAddModal
                    open={showAddSkill}
                    onClose={() => setShowAddSkill(false)}
                    levels={LEVELS}
                    onSave={(s) => {
                      setForm((prev) => ({
                        ...prev,
                        skills: [...prev.skills, s],
                      }));
                      setShowAddSkill(false);
                    }}
                  />
                </div>
              ),
            },
            {
              id: "raci",
              label: "RACI",
              content: (
                <RaciMatrix
                  mode="manage"
                  entityType="task"
                  tasks={allTasks}
                  rows={raciRows}
                  onChange={setRaciRows}
                  jobId={routeJobId}
                  label={form.name}
                />
              ),
            },
            {
              id: "paei",
              label: "PAEI",
              content: (
                <DimensionAllocationPanel
                  title="PAEI Management Style"
                  dimensions={PAEI_DIMENSIONS}
                  values={paeiValues}
                  onChange={setPaeiField}
                />
              ),
            },
            {
              id: "capi",
              label: "CAPI",
              content: (
                <DimensionAllocationPanel
                  title="CAPI — Authority, Power & Influence"
                  subtitle="How much formal authority, resource control, and persuasive influence this job carries."
                  dimensions={CAPI_DIMENSIONS}
                  values={capiValues}
                  onChange={setCapiField}
                />
              ),
            },
            {
              id: "description",
              label: "Description",
              content: (
                <div className="role-card">
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      marginBottom: 10,
                    }}
                  >
                    <button
                      type="button"
                      className="secondary-btn"
                      onClick={handleGenerateDescription}
                      disabled={generatingDesc}
                      title={
                        raciRows.length === 0
                          ? "Assign tasks in the RACI tab first"
                          : "Generate a description from your assigned RACI tasks"
                      }
                    >
                      {generatingDesc ? "Generating..." : "✨ Generate Description"}
                    </button>
                  </div>
                  <RichTextEditor
                    label="Job Description *"
                    value={form.description}
                    onChange={(val) => setField("description", val)}
                    height={280}
                  />
                  {raciRows.length === 0 && (
                    <p style={{ fontSize: 13, color: "var(--gray-400)", marginTop: 8 }}>
                      Tip: Assign tasks in the RACI tab first, then come back here to auto-generate the description.
                    </p>
                  )}
                </div>
              ),
            },
          ]}
        />
      </div>
    </div>
  );
};

export default JobEditPage;
